// Tuya Testing — mobile. Port of the Flutter `TuyaTestingPage`.
//
// The production bench: the devices that spoke to the broker most recently,
// regardless of owner, room or location. A board flashed thirty seconds ago is
// at the top of this list with no account and no mapping — which is exactly why
// it is permission-gated and not reachable from any customer path.
//
// The permission is the Users flag AllowTestingTuya (plus Super Admin), NOT
// IsSuperAdmin alone — see AppContext's `canTuyaTest`. Gating it on IsSuperAdmin
// hid the bench from every operator the backend had actually granted it to.
//
// EVERYTHING BEHIND IT IS SHARED with the desktop page: core/hooks/useTuyaBench
// holds the list, the subscriptions and the commands, and ui/shared/tuya holds
// the card. This file is chrome and layout, nothing else.

import React, { useMemo, useState } from 'react';
import {
  Box, Button, Typography, CircularProgress, IconButton, TextField, MenuItem,
  Chip, Badge, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { CenterTitleBar } from '../components/MobileAppBar';
import { useApp } from '../../../core/state/AppContext';
import { useTuyaBench, TUYA_LIMITS } from '../../../core/hooks/useTuyaBench';
import { TUYA_SEEN_WINDOWS } from '../../../core/devices/tuyaTesting';
import TuyaDeviceCard from '../../shared/tuya/TuyaDeviceCard';
import DropLogDialog from '../../shared/tuya/DropLogDialog';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';

export default function TuyaTestingScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const { userId, canTuyaTest } = useApp();

  const [limit, setLimit] = useState(10);
  const [typeFilter, setTypeFilter] = useState(0);
  const [seenWithin, setSeenWithin] = useState(null); // ms, null = any
  const [updateOpen, setUpdateOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const bench = useTuyaBench({ userId, limit, deviceTypeFilter: typeFilter });

  // ⚠️ A FILTER, NOT A DELETION — the header count stays the full list, so a
  // narrow window never reads as devices having vanished.
  const shown = useMemo(() => {
    const all = bench.rows || [];
    if (seenWithin == null) return all;
    return all.filter((r) => {
      const l = bench.liveness[String(r.UniqueNumber)];
      return l?.age != null && l.age <= seenWithin;
    });
  }, [bench.rows, bench.liveness, seenWithin]);

  // Memoised on the ROWS: this screen re-renders on every MQTT frame, and an
  // inline scope literal would make the dialog re-run its liveness probe about
  // once a second.
  const updateScope = useMemo(() => ({
    type: 'list',
    name: 'Tuya bench',
    devices: bench.rows || [],
  }), [bench.rows]);

  if (!canTuyaTest) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back }}>
        <CenterTitleBar title="TuyaTesting" showBack />
        <Typography sx={{ color: c.text, p: 3, textAlign: 'center' }}>
          This screen is for administrators.
        </Typography>
      </Box>
    );
  }

  const count = (bench.rows || []).length;

  const pillSx = {
    bgcolor: c.button, color: c.text, textTransform: 'none', borderRadius: 999,
    px: 2.5, py: 1, fontSize: 15,
    '&:hover': { bgcolor: c.button, filter: 'brightness(1.2)' },
  };

  const selectSx = {
    minWidth: 110,
    '& .MuiInputBase-input': { color: c.text, fontSize: 13 },
    '& .MuiInput-underline:before': { borderBottomColor: `${c.text}66` },
    '& .MuiSvgIcon-root': { color: c.text },
  };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar
        title="TuyaTesting"
        showBack
        actions={(
          <>
            <IconButton onClick={() => setLogOpen(true)} sx={{ color: c.text }}>
              <Badge
                badgeContent={bench.dropEvents.filter((e) => e.kind === 'drop').length}
                color="error"
              >
                <LinkOffIcon />
              </Badge>
            </IconButton>
            <IconButton onClick={bench.reload} sx={{ color: c.text }}>
              <RefreshIcon />
            </IconButton>
          </>
        )}
      />

      {/* The two counts are the Dart's: they say how many devices ONE tap will
          move, which on a bench with a rack of boards is the thing worth
          knowing before pressing it. */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', px: 1.5, pt: 1.5, flexWrap: 'wrap' }}>
        <Button sx={pillSx} onClick={bench.allOnEverything} disabled={!count}>
          {`All On(${count})`}
        </Button>
        <Button sx={pillSx} onClick={bench.allOffEverything} disabled={!count}>
          {`All Off(${count})`}
        </Button>
        {/* The third fleet action, and the only one that is not undoable — so
            unlike All On/All Off it does NOT fire on click. It opens the same
            confirmation the room and location rollouts use: one row per device,
            what firmware each is on, whether it needs the update at all, and a
            tick box per row with select-all. */}
        <Button
          sx={pillSx} disabled={!count}
          startIcon={<SystemUpdateAltIcon />}
          onClick={() => setUpdateOpen(true)}
        >
          {`Update Code(${count})`}
        </Button>
      </Box>

      {/* The liveness summary, and the reason the screen ticks: these move on
          their own as devices go quiet, with nothing arriving to move them.
          `late` is one missed heartbeat; `gone` is past the app's own
          ten-minute offline window. */}
      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center', px: 1.5, pt: 1 }}>
        <Chip size="small" label={`live ${bench.counts.live}`} sx={{ height: 20, fontSize: 11, bgcolor: '#4CAF5033', color: '#A5D6A7' }} />
        <Chip size="small" label={`late ${bench.counts.late}`} sx={{ height: 20, fontSize: 11, bgcolor: '#FFB74D33', color: '#FFCC80' }} />
        <Chip size="small" label={`gone ${bench.counts.gone + bench.counts.never}`} sx={{ height: 20, fontSize: 11, bgcolor: '#E5393533', color: '#EF9A9A' }} />
      </Box>

      {/* Limit and Type are request parameters the endpoint already takes — the
          Dart hardcodes LimitCount 10 and DeviceID 0. Seen is client-side: it
          hides rows, it does not re-query. */}
      <Box sx={{ display: 'flex', gap: 1.5, px: 1.5, pt: 1 }}>
        <TextField
          select variant="standard" label="Seen" value={seenWithin ?? ''}
          onChange={(e) => setSeenWithin(e.target.value === '' ? null : Number(e.target.value))}
          sx={selectSx}
          InputLabelProps={{ sx: { color: `${c.text}99`, fontSize: 13 } }}
          SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: c.button } } } }}
        >
          {TUYA_SEEN_WINDOWS.map((w) => (
            <MenuItem key={w.label} value={w.ms ?? ''} sx={{ color: c.text, fontSize: 13 }}>
              {w.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, px: 1.5, pt: 1 }}>
        <TextField
          select variant="standard" label="Show" value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          sx={selectSx}
          InputLabelProps={{ sx: { color: `${c.text}99`, fontSize: 13 } }}
          SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: c.button } } } }}
        >
          {TUYA_LIMITS.map((n) => (
            <MenuItem key={n} value={n} sx={{ color: c.text, fontSize: 13 }}>{n}</MenuItem>
          ))}
        </TextField>
        <TextField
          select variant="standard" label="Type" value={typeFilter} fullWidth
          onChange={(e) => setTypeFilter(Number(e.target.value))}
          sx={selectSx}
          InputLabelProps={{ sx: { color: `${c.text}99`, fontSize: 13 } }}
          SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: c.button } } } }}
        >
          <MenuItem value={0} sx={{ color: c.text, fontSize: 13 }}>All types</MenuItem>
          {bench.types.map((t) => (
            <MenuItem key={t.DeviceID} value={Number(t.DeviceID)} sx={{ color: c.text, fontSize: 13 }}>
              {t.DeviceName}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {bench.rows === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress sx={{ color: c.text }} />
          </Box>
        )}
        {bench.error && (
          <Typography sx={{ color: '#EF9A9A', textAlign: 'center', py: 2 }}>{bench.error}</Typography>
        )}
        {bench.rows !== null && !bench.rows.length && !bench.error && (
          <Typography sx={{ color: `${c.text}99`, textAlign: 'center', py: 4 }}>
            No devices have reported.
          </Typography>
        )}
        {/* Says the list was FILTERED, never just goes empty — an empty bench
            and a filtered-out bench look identical, and only one is a problem. */}
        {bench.rows !== null && bench.rows.length > 0 && shown.length === 0 && (
          <Typography sx={{ color: `${c.text}99`, textAlign: 'center', py: 4 }}>
            {`None of the ${bench.rows.length} devices has been heard from that recently — widen "Seen".`}
          </Typography>
        )}
        {shown.map((row) => (
          <TuyaDeviceCard
            key={row.UniqueNumber}
            row={row}
            types={bench.types}
            values={bench.values[String(row.UniqueNumber)] || []}
            message={bench.messages[String(row.UniqueNumber)] || ''}
            live={bench.liveness[String(row.UniqueNumber)]}
            onToggleChannel={bench.toggleChannel}
            onFanSpeed={bench.setFanSpeed}
            onAllOn={bench.allOn}
            onAllOff={bench.allOff}
            onUnlock={bench.unlock}
            onDeviceDetails={bench.deviceDetails}
            onUpdateCode={bench.updateCode}
            onChangeType={bench.changeType}
          />
        ))}
      </Box>

      <UpdateCodeDialog
        open={updateOpen}
        scope={updateScope}
        onClose={() => setUpdateOpen(false)}
      />

      <DropLogDialog
        open={logOpen}
        events={bench.dropEvents}
        onClear={bench.clearDropEvents}
        onClose={() => setLogOpen(false)}
      />
    </Box>
  );
}
